import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  RouterProvider,
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import "./styles.css";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AcceptInvite from "./pages/AcceptInvite";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Insights from "./pages/Insights";
import Search from "./pages/Search";
import Settings from "./pages/settings";
import Operations from "./pages/Operations";
import Monitoring from "./pages/Monitoring";
import Recordings from "./pages/Recordings";
import Downloads from "./pages/Downloads";
import Notifications from "./pages/Notifications";
import DevCaptions from "./pages/DevCaptions";
import Alerts from "./pages/Alerts";
import { api } from "./api";
import { rememberClaim } from "./claim";
import RegisterBox from "./pages/RegisterBox";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, staleTime: 30_000 } },
});

async function requireSession() {
  try {
    await api.me();
  } catch {
    throw redirect({ to: "/login" });
  }
}

// Guard dev-only routes. Isolate the session fetch so a genuine auth failure
// redirects to /login, while a wrong-role user is bounced to the home page
// (the redirect throw must escape the try/catch, not be swallowed).
async function requireStaff() {
  let me;
  try {
    me = await api.me();
  } catch {
    throw redirect({ to: "/login" });
  }
  if (!me.is_staff) {
    throw redirect({ to: "/" });
  }
}

async function requireDev() {
  let me;
  try {
    me = await api.me();
  } catch {
    throw redirect({ to: "/login" });
  }
  if (!(me.is_staff && me.role === "dev")) {
    throw redirect({ to: "/" });
  }
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// The box label QR. Signed in: add the box from Settings. Signed out: a new
// customer creates their account with it (sign-in stays one link away).
const claimRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/claim",
  beforeLoad: async () => {
    rememberClaim(decodeURIComponent(window.location.hash.replace(/^#/, "")));
    let signedIn = true;
    try {
      await api.me();
    } catch {
      signedIn = false;
    }
    if (!signedIn) throw redirect({ to: "/signup" });
    throw redirect({ to: "/settings", search: { section: "connectors" } });
  },
  component: () => null,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: Signup,
});

const boxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/box",
  component: RegisterBox,
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  component: AcceptInvite,
});

const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset",
  component: ResetPassword,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: Login,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: Layout,
  beforeLoad: requireSession,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: Dashboard,
});
const insightsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/insights",
  component: Insights,
});
const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: Search,
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  validateSearch: (search: Record<string, unknown>) => ({
    section: typeof search.section === "string" ? search.section : undefined,
  }),
  component: Settings,
});
const operationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/operations",
  beforeLoad: requireDev,
  component: Operations,
});
// Legacy /status path -> merged Operations page (dev-only lands there; a
// non-dev is bounced to "/" by the Operations guard).
const statusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/status",
  beforeLoad: () => {
    throw redirect({ to: "/operations" });
  },
  component: () => null,
});
const monitoringRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/monitoring",
  component: Monitoring,
});
const recordingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/recordings",
  validateSearch: (search: Record<string, unknown>) => ({
    camera: typeof search.camera === "string" ? search.camera : undefined,
  }),
  component: Recordings,
});
const downloadsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/downloads",
  component: Downloads,
});
const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notifications",
  beforeLoad: requireStaff,
  component: Notifications,
});
const alertsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/alerts",
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === "policies" ? "policies" : undefined,
  }),
  component: Alerts,
});
const policyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/policy",
  beforeLoad: () => {
    throw redirect({ to: "/alerts", search: { tab: "policies" } });
  },
  component: () => null,
});
const policiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/policies",
  beforeLoad: () => {
    throw redirect({ to: "/alerts", search: { tab: "policies" } });
  },
  component: () => null,
});
const devCaptionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dev/captions",
  beforeLoad: requireDev,
  component: DevCaptions,
});

// Legacy paths still in muscle memory — redirect rather than 404.
const legacyCamerasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cameras",
  beforeLoad: () => {
    throw redirect({ to: "/settings", search: { section: undefined } });
  },
  component: () => null,
});
const legacyConfigRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/config",
  beforeLoad: () => {
    throw redirect({ to: "/settings", search: { section: undefined } });
  },
  component: () => null,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  claimRoute,
  signupRoute,
  boxRoute,
  inviteRoute,
  resetRoute,
  appRoute.addChildren([
    searchRoute,
    dashboardRoute,
    insightsRoute,
    monitoringRoute,
    recordingsRoute,
    downloadsRoute,
    notificationsRoute,
    alertsRoute,
    policyRoute,
    policiesRoute,
    settingsRoute,
    operationsRoute,
    devCaptionsRoute,
    statusRoute,
    legacyCamerasRoute,
    legacyConfigRoute,
  ]),
]);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>,
);
